import { Stack } from "@mui/material"
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { setShowLoading } from "store/sysSlice";
import http from "../../../http"
import { apiUrl } from "config/settings";
import { endpointName, ST_TYPE_ID, YESH_INVOICE_DOCUMENT_TYPE } from "../const";
import { BIZ_STATUS } from "config/const";
import NullCheck from "components/NullCheck";
import BizsDetails from "./dialogs/BizsDetails";
import McsDetails from "./dialogs/McsDetails";
import moment from "moment";
import Bizs from "./cards/bizs";
import Mcs from "./cards/Mcs";
import ServiceCall from "./cards/ServiceCall";
import ServiceCallDetails from "./dialogs/ServiceCallDetails";
import Payments from "./cards/payments";
import PaymentsDetails from "./dialogs/PaymentsDetails";
import Documents from "./cards/Documents";
import DocumentsDetails from "./dialogs/DocumentsDetails";
import { currencyFormat, sum } from "config/functions";
import RevenueForecast from "./dialogs/RevenueForecast";
import Retainers from "./cards/Retainers";

const System = () => {
    const dispatch = useDispatch()
    const [data, setData] = useState(null)
    const [bizDetails, setBizDetails] = useState(null)
    const [mcDetails, setMcDetails] = useState(null)
    const [dialogTitle, setDialogTitle] = useState(null)
    const [serviceCallDetails, setServiceCallDetails] = useState(null)
    const [paymentsDetails, setPaymentsDetails] = useState(null)
    const [documentsDetails, setDocumentsDetails] = useState(null)
    const [revenueForecast, setRevenueForecast] = useState(null)

    useEffect(() => {
        getData();
        // if (!intervalRef.current)
        //     intervalRef.current = setInterval(() => getData(), 10 * 60 * 1000);

        // return () => {
        //     if (intervalRef.current) {
        //         clearInterval(intervalRef.current);
        //         intervalRef.current = null;
        //     }
        // };
    }, []);

    const getData = async () => {
        try {
            dispatch(setShowLoading(true))
            const data = await http.get(`${apiUrl}/${endpointName}/systemData`);
            prepareData(data?.data)
        }
        catch (err) {
            dispatch(setShowLoading(false))
            console.error("monthly effective error:", err);
        }
    }

    const getAnnualIncome = (documents = {}) => {
        const taxInvoices = documents.tax_invoice ?? [];
        const creditNotes = documents.credit_note ?? [];
        const invoices = documents.invoice ?? [];
        const usedDocs = [];

        // חשבוניות מס
        const taxInvoicesSum = taxInvoices.reduce((sum, doc) => {
            const price = Number(doc?.TotalPrice || 0);
            usedDocs.push(doc);
            return sum + price;
        }, 0);

        // חשבוניות עסקה פתוחות בלבד
        const openInvoicesSum = invoices
            .filter(doc => doc?.IsApproved === false)
            .reduce((sum, doc) => {
                const price = Number(doc?.TotalPrice || 0);
                usedDocs.push(doc);
                return sum + price;
            }, 0);

        // זיכויים
        const creditNotesSum = creditNotes.reduce((sum, doc) => {
            const price = Number(doc?.TotalPrice || 0);
            usedDocs.push(doc)
            return sum + price;
        }, 0);

        const total = Math.round(
            (taxInvoicesSum + openInvoicesSum - creditNotesSum) * 100
        ) / 100;

        return {
            total,
            breakdown: {
                taxInvoices: taxInvoicesSum,
                openInvoices: openInvoicesSum,
                creditNotes: creditNotesSum
            },
            documents: usedDocs.map(doc => ({
                ...doc,
                id: doc?.ID,
                price: doc?.TotalPrice,
                customerName: doc?.CustomerName
            }))
        };
    };

    const buildMonthlyRevenueForecast = (retainers = [], oneTimeDocuments = [], year = moment().year()) => {
        const months = Array.from({ length: 12 }, (_, index) => ({
            month: index + 1,
            revenue: {
                retainer: 0,
                oneTime: 0,
                total: 0,
            },
            lists: {
                retainers: [],
                oneTime: [],
                total: [],
            },
        }));

        // ריטיינרים
        retainers.forEach((retainer) => {
            const start = moment(retainer?.startDate, "DD-MM-YYYY", true);
            const end = moment(retainer?.endDate, "DD-MM-YYYY", true);
            const interval = Number(retainer?.interval);
            const price = Number(retainer?.price || 0);

            if (!start.isValid() || !end.isValid() || !interval || interval <= 0) return;

            let current = start.clone();

            while (current.isSameOrBefore(end)) {
                if (current.year() === year) {
                    const monthIndex = current.month();

                    months[monthIndex].revenue.retainer += price;
                    months[monthIndex].lists.retainers.push({
                        ...retainer,
                        chargeDate: current.format("DD-MM-YYYY"),
                    });
                }

                current.add(interval, "months");
            }
        });

        // חד פעמיים
        const mapDocType = (type) => {
            if (type === YESH_INVOICE_DOCUMENT_TYPE?.TAX_INVOICE) return "tax_invoice";
            if (type === YESH_INVOICE_DOCUMENT_TYPE?.CREDIT_NOTE) return "credit_note";
            if (type === YESH_INVOICE_DOCUMENT_TYPE?.INVOICE) return "invoice";
            return null;
        };

        const docsByMonth = Array.from({ length: 12 }, () => ({
            tax_invoice: [],
            credit_note: [],
            invoice: [],
        }));

        const isSameCustomer = (ret, doc) => {
            const retCustomerId = ret?.customerId ?? ret?.CustomerID ?? ret?.customer?.id ?? null;
            const docCustomerId = doc?.CustomerID ?? doc?.customerId ?? doc?.customer?.id ?? null;

            if (retCustomerId != null && docCustomerId != null) {
                return String(retCustomerId) === String(docCustomerId);
            }

            return String(ret?.customerName || "").trim() === String(doc?.CustomerName || "").trim();
        };

        oneTimeDocuments.forEach((doc) => {
            const date = moment(doc?.Date, "DD-MM-YYYY", true);

            if (!date.isValid()) return;
            if (date.year() !== year) return;
            if (doc?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE?.RECEIPT) return;

            const monthIndex = date.month();
            const key = mapDocType(doc?.DocumentType);

            if (!key) return;

            // לא להכניס מסמך שכבר נספר כריטיינר
            const isRetainerMatch = months[monthIndex].lists.retainers.some((ret) => {
                const retChargeDate = moment(ret?.chargeDate, "DD-MM-YYYY", true);
                const docDate = moment(doc?.Date, "DD-MM-YYYY", true);

                return (
                    retChargeDate.isValid() &&
                    docDate.isValid() &&
                    retChargeDate.isSame(docDate, "day") &&
                    Number(ret?.price || 0) === Number(doc?.TotalPrice || 0) &&
                    isSameCustomer(ret, doc)
                );
            });

            if (isRetainerMatch) return;

            docsByMonth[monthIndex][key].push(doc);
        });

        docsByMonth.forEach((monthDocs, monthIndex) => {
            const incomeData = getAnnualIncome(monthDocs);

            months[monthIndex].revenue.oneTime = Number(incomeData?.total || 0);
            months[monthIndex].lists.oneTime = (incomeData?.documents || []).map((doc) => ({
                ...doc,
                chargeDate: doc?.Date,
            }));
        });

        // חישוב total + עיגול + רשימות total
        const normalizedMonths = months.map((m) => {
            const retainer = Math.round(m.revenue.retainer);
            const oneTime = Math.round(m.revenue.oneTime);
            const total = Math.round(retainer + oneTime);

            return {
                ...m,
                revenue: {
                    retainer,
                    oneTime,
                    total,
                },
                lists: {
                    ...m.lists,
                    total: [...m.lists.retainers, ...m.lists.oneTime],
                },
            };
        });

        const buildRow = (id, name, revenueKey, listKey) => {
            const monthlyValues = {};
            const monthDetails = {};

            normalizedMonths.forEach((monthItem) => {
                const monthNumber = monthItem.month;
                const value = Number(monthItem.revenue?.[revenueKey] || 0);
                const documents = monthItem.lists?.[listKey] || [];

                monthlyValues[monthNumber] = value;
                monthDetails[monthNumber] = {
                    month: monthNumber,
                    value,
                    documents,
                    count: documents.length,
                };
            });

            const summary =
                Math.round(
                    normalizedMonths.reduce(
                        (sum, monthItem) => sum + Number(monthItem.revenue?.[revenueKey] || 0),
                        0
                    ) * 100
                ) / 100;

            return {
                id,
                name,
                ...monthlyValues,
                summary,
                monthDetails,
            };
        };

        return [
            buildRow("retainer", "RETAINER", "retainer", "retainers"),
            buildRow("oneTime", "ONE_TIME", "oneTime", "oneTime"),
            buildRow("total", "SUM", "total", "total"),
        ];
    };

    const buildMonthlyExpensesForecast = (
        expenses = [],
        retainers = [],
        oneTimeDocuments = [],
        year = moment().year()
    ) => {
        const VAT_PERCENT = 18;

        const round2 = (num) => Math.round(Number(num || 0) * 100) / 100;

        const isVatDocument = (item) => {
            if (item?.DocumentType !== YESH_INVOICE_DOCUMENT_TYPE.INVOICE &&
                item?.DocumentType !== YESH_INVOICE_DOCUMENT_TYPE.TAX_INVOICE
            ) return false

            const firstItem = item?.items?.[0];
            const vatType =
                firstItem?.VatType ??
                firstItem?.vattype ??
                firstItem?.vatType ??
                item?.VatType ??
                item?.vattype ??
                item?.vatType;

            return Number(vatType) === 2 || Number(vatType) === 1;
        };

        const extractVat = (total) => {
            const t = Number(total || 0);
            if (!t) return 0;

            return round2(t - t / (1 + VAT_PERCENT / 100));
        };

        const getProviderId = (item) =>
            String(
                item?.ProviderID ??
                item?.providerId ??
                item?.providerID ??
                item?.provider?.id ??
                ""
            ).trim();

        const getExpenditureId = (item) =>
            String(
                item?.Expenditure ??
                item?.expenditure ??
                item?.ExpenditureID ??
                item?.expenditureId ??
                ""
            ).trim();

        const isSameFixedVariableExpense = (fixedExp, variableExp) => {
            const fixedProviderId = getProviderId(fixedExp);
            const variableProviderId = getProviderId(variableExp);

            const fixedExpenditureId = getExpenditureId(fixedExp);
            const variableExpenditureId = getExpenditureId(variableExp);

            return (
                !!fixedProviderId &&
                !!variableProviderId &&
                !!fixedExpenditureId &&
                !!variableExpenditureId &&
                fixedProviderId === variableProviderId &&
                fixedExpenditureId === variableExpenditureId
            );
        };

        const months = Array.from({ length: 12 }, (_, index) => ({
            month: index + 1,
            expense: { fixed: 0, variable: 0, vat: 0, total: 0 },
            lists: { fixed: [], variable: [], vat: [], total: [] },
        }));

        // =========================
        // שלב 1 - קודם מכניסים את כל ההוצאות המשתנות
        // =========================
        expenses.forEach((exp) => {
            const date = moment(exp?.Date, "DD-MM-YYYY", true);

            if (!date.isValid()) return;
            if (date.year() !== year) return;
            if (exp?.isRetainer) return;

            const monthIndex = date.month();
            const price = Number(exp?.Price || 0);

            const normalizedExp = {
                ...exp,
                chargeDate: exp?.Date,
                sourceType: "expense-variable",
            };

            months[monthIndex].expense.variable += price;
            months[monthIndex].lists.variable.push(normalizedExp);
        });

        // =========================
        // שלב 2 - הוצאות קבועות
        // אם באותו חודש קיימת משתנה עם אותו ProviderID + Expenditure
        // מעלים את המשתנה לקבוע ולא מוסיפים את הקבוע של אותו חודש
        // =========================
        expenses.forEach((exp) => {
            const date = moment(exp?.Date, "DD-MM-YYYY", true);

            if (!date.isValid()) return;
            if (date.year() !== year) return;
            if (!exp?.isRetainer) return;

            const startMonthIndex = date.month();
            const fixedPrice = Number(exp?.Price || 0);

            const normalizedFixedExp = {
                ...exp,
                chargeDate: exp?.Date,
                sourceType: "expense-fixed",
            };

            for (let i = startMonthIndex; i < 12; i++) {
                const variableList = months[i].lists.variable || [];

                const matchingVariableIndex = variableList.findIndex((variableExp) =>
                    isSameFixedVariableExpense(exp, variableExp)
                );

                if (matchingVariableIndex !== -1) {
                    const matchedVariable = variableList[matchingVariableIndex];
                    const variablePrice = Number(matchedVariable?.Price || 0);

                    // מורידים מהמשתנות
                    months[i].expense.variable -= variablePrice;
                    months[i].lists.variable.splice(matchingVariableIndex, 1);

                    // מעלים את המשתנה לקבוע עם הסכום המעודכן שלו
                    months[i].expense.fixed += variablePrice;
                    months[i].lists.fixed.push({
                        ...matchedVariable,
                        isRetainer: true,
                        chargeDate: matchedVariable?.Date || date.clone().month(i).format("DD-MM-YYYY"),
                        sourceType: "variable-promoted-to-fixed",
                        originalFixedExpense: normalizedFixedExp,
                    });

                    // לא מוסיפים את ההוצאה הקבועה המקורית לחודש הזה
                    continue;
                }

                // אם אין משתנה תואמת - מוסיפים את הקבועה רגיל
                months[i].expense.fixed += fixedPrice;
                months[i].lists.fixed.push({
                    ...normalizedFixedExp,
                    chargeDate: date.clone().month(i).format("DD-MM-YYYY"),
                });
            }
        });

        // =========================
        // בניית ריטיינרים לפי חודש
        // בשביל למנוע כפילות מול oneTimeDocuments
        // =========================
        const retainersByMonth = Array.from({ length: 12 }, () => []);

        retainers.forEach((retainer) => {
            const start = moment(retainer?.startDate, "DD-MM-YYYY", true);
            const end = moment(retainer?.endDate, "DD-MM-YYYY", true);
            const interval = Number(retainer?.interval);

            if (!start.isValid() || !end.isValid() || !interval || interval <= 0) return;

            let current = start.clone();

            while (current.isSameOrBefore(end)) {
                if (current.year() === year) {
                    const monthIndex = current.month();

                    retainersByMonth[monthIndex].push({
                        ...retainer,
                        chargeDate: current.format("DD-MM-YYYY"),
                    });
                }

                current.add(interval, "months");
            }
        });

        const isSameCustomer = (ret, doc) => {
            const retCustomerId = ret?.customerId ?? ret?.CustomerID ?? ret?.customer?.id ?? null;
            const docCustomerId = doc?.CustomerID ?? doc?.customerId ?? doc?.customer?.id ?? null;

            if (retCustomerId != null && docCustomerId != null) {
                return String(retCustomerId) === String(docCustomerId);
            }

            return String(ret?.customerName || "").trim() === String(doc?.CustomerName || "").trim();
        };

        // =========================
        // מע״מ מריטיינרים
        // =========================
        retainers.forEach((retainer) => {
            const start = moment(retainer?.startDate, "DD-MM-YYYY", true);
            const end = moment(retainer?.endDate, "DD-MM-YYYY", true);
            const interval = Number(retainer?.interval);
            const price = Number(retainer?.price || 0);

            if (!start.isValid() || !end.isValid() || !interval || interval <= 0) return;
            if (retainer?.documentType !== YESH_INVOICE_DOCUMENT_TYPE.INVOICE) return;

            let current = start.clone();

            while (current.isSameOrBefore(end)) {
                if (current.year() === year) {
                    const monthIndex = current.month();
                    const vatAmount = extractVat(price);

                    if (vatAmount > 0) {
                        months[monthIndex].expense.vat += vatAmount;
                        months[monthIndex].lists.vat.push({
                            ...retainer,
                            chargeDate: current.format("DD-MM-YYYY"),
                            vatAmount,
                            sourceType: "retainer",
                        });
                    }
                }

                current.add(interval, "months");
            }
        });

        // =========================
        // מע״מ ממסמכים חד פעמיים
        // עם מניעת כפילות מול ריטיינרים
        // =========================
        oneTimeDocuments.forEach((doc) => {
            const date = moment(doc?.Date, "DD-MM-YYYY", true);

            if (!date.isValid()) return;
            if (date.year() !== year) return;
            if (doc?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE?.RECEIPT) return;

            const monthIndex = date.month();

            const isRetainerMatch = retainersByMonth[monthIndex].some((ret) => {
                const retChargeDate = moment(ret?.chargeDate, "DD-MM-YYYY", true);
                const docDate = moment(doc?.Date, "DD-MM-YYYY", true);

                return (
                    retChargeDate.isValid() &&
                    docDate.isValid() &&
                    retChargeDate.isSame(docDate, "day") &&
                    Number(ret?.price || 0) === Number(doc?.TotalPrice || 0) &&
                    isSameCustomer(ret, doc)
                );
            });

            if (isRetainerMatch) return;
            if (!isVatDocument(doc)) return;

            let vatAmount = extractVat(doc?.TotalPrice || 0);

            if (doc?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE?.CREDIT_NOTE) {
                vatAmount *= -1;
            }

            if (!vatAmount) return;

            months[monthIndex].expense.vat += vatAmount;
            months[monthIndex].lists.vat.push({
                ...doc,
                chargeDate: doc?.Date,
                vatAmount,
                sourceType: "oneTime",
            });
        });

        // =========================
        // נרמול
        // =========================
        const normalizedMonths = months.map((m) => {
            const fixed = round2(m.expense.fixed);
            const variable = round2(m.expense.variable);
            const vat = round2(m.expense.vat);
            const total = round2(fixed + variable + vat);

            return {
                ...m,
                expense: {
                    fixed,
                    variable,
                    vat,
                    total,
                },
                lists: {
                    fixed: m.lists.fixed,
                    variable: m.lists.variable,
                    vat: m.lists.vat,
                    total: [...m.lists.fixed, ...m.lists.variable, ...m.lists.vat],
                },
            };
        });

        const buildRow = (id, name, expenseKey, listKey) => {
            const monthlyValues = {};
            const monthDetails = {};

            normalizedMonths.forEach((monthItem) => {
                const monthNumber = monthItem.month;
                const value = Number(monthItem.expense?.[expenseKey] || 0);
                const documents = monthItem.lists?.[listKey] || [];

                monthlyValues[monthNumber] = value;
                monthDetails[monthNumber] = {
                    month: monthNumber,
                    value,
                    documents,
                    count: documents.length,
                };
            });

            const summary = round2(
                normalizedMonths.reduce(
                    (sum, monthItem) => sum + Number(monthItem.expense?.[expenseKey] || 0),
                    0
                )
            );

            return {
                id,
                name,
                ...monthlyValues,
                summary,
                monthDetails,
            };
        };

        return [
            buildRow("fixed", "FIXED_EXPENSES", "fixed", "fixed"),
            buildRow("variable", "VARIABLE_EXPENSES", "variable", "variable"),
            buildRow("vat", "VAT_EXPENSES", "vat", "vat"),
            buildRow("total", "SUM", "total", "total"),
        ];
    };

    const buildCustomerProfitRows = (documents = [], year = moment().year(), options = {}) => {
        const {
            dateFormat = "DD-MM-YYYY",
            getCustomerId = (doc) =>
                doc?.CustomerNumberID || doc?.customerNumberID || doc?.CustomerName || "unknown",
            getCustomerName = (doc) =>
                doc?.CustomerName || doc?.customerName || "ללא שם",
            getDocumentSign = (doc) => {
                return 1;
            },
        } = options;

        const customersMap = {};

        documents.forEach((doc) => {
            const date = moment(doc?.Date, dateFormat, true);

            if (!date.isValid()) return;
            if (date.year() !== year) return;
            if (doc?.DocumentType !== YESH_INVOICE_DOCUMENT_TYPE.INVOICE &&
                doc?.DocumentType !== YESH_INVOICE_DOCUMENT_TYPE.TAX_INVOICE &&
                doc?.DocumentType !== YESH_INVOICE_DOCUMENT_TYPE.SERVICE_CALL) return;

            const month = date.month() + 1;
            const customerId = String(getCustomerId(doc));
            const customerName = getCustomerName(doc);
            const sign = getDocumentSign(doc);

            const items = Array.isArray(doc?.items) ? doc.items : [];

            const revenue = items.reduce((sum, item) => {
                const quantity = Number(item?.quantity || 1);
                const price = Number(item?.price || 0);

                if (doc?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.SERVICE_CALL)
                    return sum + (-price * quantity * sign);
                else
                    return sum + (price * quantity * sign);
            }, 0);

            const cost = items.reduce((sum, item) => {
                const quantity = Number(item?.quantity || 1);
                const priceCost = Number(item?.priceCost || 0);
                return sum + (priceCost * quantity * sign);
            }, 0);

            const profit = revenue - cost;

            if (!customersMap[customerId]) {
                const baseMonths = {};
                const monthDetails = {};

                for (let i = 1; i <= 12; i++) {
                    baseMonths[i] = 0;
                    monthDetails[i] = {
                        revenue: 0,
                        cost: 0,
                        profit: 0,
                        documents: [],
                    };
                }

                customersMap[customerId] = {
                    ...baseMonths,
                    id: customerId,
                    name: customerName,
                    customerId,
                    totalRevenue: 0,
                    totalCost: 0,
                    summary: 0,
                    monthDetails,
                };
            }

            customersMap[customerId][month] += profit;
            customersMap[customerId].totalRevenue += revenue;
            customersMap[customerId].totalCost += cost;
            customersMap[customerId].summary += profit;

            customersMap[customerId].monthDetails[month].revenue += revenue;
            customersMap[customerId].monthDetails[month].cost += cost;
            customersMap[customerId].monthDetails[month].profit += profit;
            customersMap[customerId].monthDetails[month].documents.push({
                Date: doc?.Date,
                DocumentType: doc?.DocumentType,
                CustomerName: doc?.CustomerName,
                CustomerNumberID: doc?.CustomerNumberID,
                revenue,
                cost,
                profit,
                items,
                originalDocument: doc,
            });
        });

        return Object.values(customersMap).map((row) => {
            for (let i = 1; i <= 12; i++) {
                row[i] = Number(row[i].toFixed(2));
                row.monthDetails[i].revenue = Number(row.monthDetails[i].revenue.toFixed(2));
                row.monthDetails[i].cost = Number(row.monthDetails[i].cost.toFixed(2));
                row.monthDetails[i].profit = Number(row.monthDetails[i].profit.toFixed(2));
            }

            row.totalRevenue = Number(row.totalRevenue.toFixed(2));
            row.totalCost = Number(row.totalCost.toFixed(2));
            row.summary = Number(row.summary.toFixed(2));

            return row;
        });
    };

    const prepareData = (data) => {
        const { bizs, mcs, servicesCalls, erp } = data
        const duplicateGroups = [];

        let currItem = {
            mcs: {
                active: [],
                notActive: [],
            },
            bizs: {
                active: [],
                pilot: [],
                notActive: [],
                blocked: [],
                newInYear: [],
                newInMonth: [],
                bizWithoutRetainer: [],
            },
            serviceCalls: {
                open: [],
                closed: [],
                waiting: [],
                toDay: [],
            },
            retainers: {
                active: [],
                notActive: [],
            },
            documentsInYear: {
                invoice: [],
                order: [],
                quote: [],
                receipt: [],
                tax_invoice: [],
                credit_note: [],
            },
            documentsInMonth: {
                invoice: [],
                order: [],
                quote: [],
                receipt: [],
                tax_invoice: [],
                credit_note: [],
            },
            DuplicateDocList: [],
            revenueForecast: [],
            expensesForecast: [],
        }

        // prepare Mc data
        mcs?.forEach(mc => {
            if (mc?.isConnected) currItem.mcs.active.push(mc)
            if (!mc?.isConnected) currItem.mcs.notActive.push(mc)
        })

        // prepare Biz data
        bizs?.forEach(biz => {
            if (biz?.status === BIZ_STATUS.ACTIVE) currItem.bizs.active.push(biz)
            if (biz?.status === BIZ_STATUS.PILOT) currItem.bizs.pilot.push(biz)
            if (biz?.status === BIZ_STATUS.NOT_ACTIVE) currItem.bizs.notActive.push(biz)
            if (biz?.status === BIZ_STATUS.BLOCKED) currItem.bizs.blocked.push(biz)

            if (biz?.created && moment(biz.created).isSame(moment(), 'year') && biz?.status === BIZ_STATUS.ACTIVE)
                currItem.bizs.newInYear.push(biz)

            if (biz?.created && moment(biz.created).isSame(moment(), 'month') && biz?.status === BIZ_STATUS.ACTIVE)
                currItem.bizs.newInMonth.push(biz)

            if (biz?.status === BIZ_STATUS.ACTIVE && !erp?.retainers?.some(retainer => `${retainer?.customerID}` === biz?.erpClientId))
                currItem.bizs.bizWithoutRetainer.push(biz)
        })

        // prepare service calls data
        servicesCalls?.forEach(sc => {
            if (sc?.stTypeId === ST_TYPE_ID.OPEN) currItem.serviceCalls.open.push(sc)
            if (sc?.stTypeId === ST_TYPE_ID.CLOSE) currItem.serviceCalls.closed.push(sc)
            if (sc?.stTypeId === ST_TYPE_ID.WAITING) currItem.serviceCalls.waiting.push(sc)
            if (sc?.stTypeId === ST_TYPE_ID.OPEN &&
                sc?.workDt &&
                moment(sc?.workDt).isSame(moment(), 'day')
            )
                currItem.serviceCalls.toDay.push(sc)
        })

        erp?.retainers?.forEach(retainer => {
            if (retainer?.statusID === 1) currItem.retainers.active.push(retainer)
            if (!retainer?.statusID === 0) currItem.retainers.notActive.push(retainer)
        })

        erp?.documents?.forEach(document => {
            if (document?.Date && moment(document.Date, "DD-MM-YYYY").isSame(moment(), "year")) {
                if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.INVOICE)
                    currItem.documentsInYear.invoice.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.ORDER)
                    currItem.documentsInYear.order.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.QUOTE)
                    currItem.documentsInYear.quote.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.RECEIPT)
                    currItem.documentsInYear.receipt.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.TAX_INVOICE)
                    currItem.documentsInYear.tax_invoice.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.CREDIT_NOTE)
                    currItem.documentsInYear.credit_note.push(document)
            }

            if (document?.Date && moment(document.Date, "DD-MM-YYYY").isSame(moment(), "month")) {
                if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.INVOICE)
                    currItem.documentsInMonth.invoice.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.ORDER)
                    currItem.documentsInMonth.order.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.QUOTE)
                    currItem.documentsInMonth.quote.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.RECEIPT)
                    currItem.documentsInMonth.receipt.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.TAX_INVOICE)
                    currItem.documentsInMonth.tax_invoice.push(document)
                else if (document?.DocumentType === YESH_INVOICE_DOCUMENT_TYPE.CREDIT_NOTE)
                    currItem.documentsInMonth.credit_note.push(document)
            }

            if (!document?.Date) return;
            if (document?.BalancePrice === 0) return;

            const sameGroup = duplicateGroups.find((group) => {
                const sample = group[0];

                const sameCustomer =
                    (sample?.CustomerName || "").trim().toLowerCase() ===
                    (document?.CustomerName || "").trim().toLowerCase();

                const sameType = sample?.DocumentType === document?.DocumentType;

                const sameDate =
                    moment(sample?.Date, "DD-MM-YYYY").isSame(
                        moment(document?.Date, "DD-MM-YYYY"),
                        "day"
                    );

                const price1 = Number(sample?.TotalPrice || 0);
                const price2 = Number(document?.TotalPrice || 0);
                const closePrice = Math.abs(price1 - price2) <= 1; // סטייה של עד שקל

                return sameCustomer && sameType && sameDate && closePrice;
            });

            if (sameGroup) {
                sameGroup.push(document);
            }
            else {
                duplicateGroups.push([document]);
            }
        })

        currItem.DuplicateDocList = duplicateGroups.filter((group) => group.length > 1)?.flat();
        const annualIncomeInYear = getAnnualIncome(currItem.documentsInYear)
        const annualIncomeInMonth = getAnnualIncome(currItem.documentsInMonth)
        const openOrders = currItem?.documentsInYear?.order?.filter(d => !d?.IsApproved)
        const buildCustomer = buildCustomerProfitRows(erp?.documents);
        currItem.revenueForecast = buildMonthlyRevenueForecast(erp?.retainers, erp?.documents)
        currItem.expensesForecast = buildMonthlyExpensesForecast(erp?.expenses, erp?.retainers, erp?.documents)

        const obj = {
            mcList: mcs,
            bizList: bizs,
            buildCustomer: buildCustomer,
            revenueForecast: currItem.revenueForecast,
            expensesForecast: currItem.expensesForecast,
            mcStatus: [
                {
                    id: 'active',
                    name: "SUM_MCS",
                    status: 1,
                    show: true,
                    onClick: (item) => setMcDetails(item),
                    list: mcs ?? [],
                    value: mcs?.length ?? 0,
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
                {
                    id: 'active',
                    name: "MCS_CONNECTED",
                    status: 2,
                    show: true,
                    onClick: (item) => setMcDetails(item),
                    list: currItem.mcs.active,
                    value: currItem.mcs.active?.length ?? 0,
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'not-Active',
                    name: "MCS_NOT_CONNECTED",
                    status: 2,
                    show: true,
                    onClick: (item) => setMcDetails(item),
                    list: currItem.mcs.notActive,
                    value: currItem.mcs.notActive?.length ?? 0,
                    iconBgColor: "#ffc8c8",
                    iconColor: "#510606"
                },
            ],
            bizStatus: [
                {
                    id: 'active',
                    name: "BUSINESS_ACTIVE",
                    status: 1,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem.bizs.active,
                    value: currItem.bizs.active?.length ?? 0,
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'pilot',
                    name: "BUSINESS_PILOT",
                    status: 3,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem.bizs.pilot,
                    value: currItem.bizs.pilot?.length ?? 0,
                    iconBgColor: "#fff4e5",
                    iconColor: "#ff9100"
                },
                {
                    id: 'not-Active',
                    name: "BUSINESS_NOT_ACTIVE",
                    status: 2,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem.bizs.notActive,
                    value: currItem.bizs.notActive?.length ?? 0,
                    iconBgColor: "#e4e4e4",
                    iconColor: "#4a4a4a"
                },
                {
                    id: 'blocked',
                    name: "BUSINESS_BLOCKED",
                    status: 4,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem.bizs.blocked,
                    value: currItem.bizs.blocked?.length ?? 0,
                    iconBgColor: "#ffc8c8",
                    iconColor: "#510606"
                },
                {
                    id: 'newInYear',
                    name: "BUSINESS_NEW_IN_YEAR",
                    status: 4,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem.bizs.newInYear,
                    value: currItem.bizs.newInYear?.length ?? 0,
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
                {
                    id: 'newInMonth',
                    name: "BUSINESS_NEW_IN_MONTH",
                    status: 4,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem.bizs.newInMonth,
                    value: currItem.bizs.newInMonth?.length ?? 0,
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
            ],
            serviceCallStatus: [
                {
                    id: 'open',
                    name: "SERVICE_CALL_OPEN",
                    status: 1,
                    show: true,
                    onClick: (item) => setServiceCallDetails(item),
                    list: currItem.serviceCalls.open,
                    value: currItem.serviceCalls.open?.length ?? 0,
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'toDay',
                    name: "SERVICE_CALL_TO_DAY",
                    status: 3,
                    show: true,
                    onClick: (item) => setServiceCallDetails(item),
                    list: currItem.serviceCalls.toDay,
                    value: currItem.serviceCalls.toDay?.length ?? 0,
                    iconBgColor: "#fff4e5",
                    iconColor: "#ff9100"
                },
                {
                    id: 'waiting',
                    name: "SERVICE_CALL_WAITING",
                    status: 2,
                    show: true,
                    onClick: (item) => setServiceCallDetails(item),
                    list: currItem.serviceCalls.waiting,
                    value: currItem.serviceCalls.waiting?.length ?? 0,
                    iconBgColor: "#e4e4e4",
                    iconColor: "#4a4a4a"
                },
            ],
            retainers: [
                {
                    id: 'activeRetainers',
                    name: "ACTIVE_RETAINERS",
                    status: 1,
                    show: true,
                    onClick: (item) => setPaymentsDetails(item),
                    list: currItem?.retainers?.active,
                    value: currItem?.retainers?.active?.length ?? 0,
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'notActiveRetainers',
                    name: "NOT_ACTIVE_RETAINERS",
                    status: 2,
                    show: true,
                    onClick: (item) => setPaymentsDetails(item),
                    list: currItem?.retainers?.notActive,
                    value: currItem?.retainers?.notActive?.length ?? 0,
                    iconBgColor: "#e4e4e4",
                    iconColor: "#4a4a4a"
                },
                {
                    id: 'bizWithoutRetainer',
                    name: "BUSINESS_WITHOUT_RETAINER",
                    status: 3,
                    show: true,
                    onClick: (item) => setBizDetails(item),
                    list: currItem?.bizs?.bizWithoutRetainer,
                    value: currItem?.bizs?.bizWithoutRetainer?.length ?? 0,
                    iconBgColor: "#ffc8c8",
                    iconColor: "#510606"
                },
            ],
            payments: [
                {
                    id: 'annualIncomeInYear',
                    name: "ANNUAL_INCOME_IN_YEAR",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: annualIncomeInYear?.documents,
                    value: currencyFormat(annualIncomeInYear?.total) ?? 0,
                    subValue: annualIncomeInYear?.documents?.length,
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
                {
                    id: 'annualIncomeInMonth',
                    name: "ANNUAL_INCOME_IN_MONTH",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: annualIncomeInMonth?.documents,
                    value: currencyFormat(annualIncomeInMonth?.total) ?? 0,
                    subValue: annualIncomeInMonth?.documents?.length,
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
                {
                    id: 'openOrders',
                    name: "OPEN_ORDERS",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: openOrders?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: openOrders?.length ?? 0,
                    value: currencyFormat(sum(openOrders, "TotalPrice")),
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
            ],
            documents: [
                {
                    id: 'receiptInYear',
                    name: "RECEIPTS",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem?.documentsInYear?.receipt?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem?.documentsInYear?.receipt.length ?? 0,
                    value: currencyFormat(sum(currItem?.documentsInYear?.receipt, "TotalPrice")),
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'invoiceInYear',
                    name: "INVOICES",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem?.documentsInYear?.invoice?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem?.documentsInYear?.invoice.length ?? 0,
                    value: currencyFormat(sum(currItem?.documentsInYear?.invoice, "TotalPrice")),
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'tax_invoiceInYear',
                    name: "TAX_INVOICES",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem?.documentsInYear?.tax_invoice?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem?.documentsInYear?.tax_invoice.length ?? 0,
                    value: currencyFormat(sum(currItem?.documentsInYear?.tax_invoice, "TotalPrice")),
                    iconBgColor: "#abf5bc",
                    iconColor: "#065118"
                },
                {
                    id: 'credit_noteInYear',
                    name: "CREDIT_NOTES",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem?.documentsInYear?.credit_note?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem?.documentsInYear?.credit_note.length ?? 0,
                    value: currencyFormat(sum(currItem?.documentsInYear?.credit_note, "TotalPrice")),
                    iconBgColor: "#ffc8c8",
                    iconColor: "#510606"
                },
                {
                    id: 'orderInYear',
                    name: "ORDERS",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem?.documentsInYear?.order?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem?.documentsInYear?.order.length ?? 0,
                    value: currencyFormat(sum(currItem?.documentsInYear?.order, "TotalPrice")),
                    iconBgColor: "#d9efff",
                    iconColor: "#0091ff"
                },
                {
                    id: 'quoteInYear',
                    name: "QUOTES",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem?.documentsInYear?.quote?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem?.documentsInYear?.quote.length ?? 0,
                    value: currencyFormat(sum(currItem?.documentsInYear?.quote, "TotalPrice")),
                    iconBgColor: "#e4e4e4",
                    iconColor: "#4a4a4a"
                },
                {
                    id: 'DuplicateDocList',
                    name: "DUPLICATE_DOC",
                    status: 2,
                    show: true,
                    onClick: (item) => setDocumentsDetails(item),
                    list: currItem.DuplicateDocList?.map(document => ({
                        ...document,
                        price: document?.TotalPrice,
                        customerName: document?.CustomerName,
                        id: document?.ID,
                    })) ?? [],
                    subValue: currItem.DuplicateDocList.length ?? 0,
                    value: currencyFormat(sum(currItem.DuplicateDocList, "TotalPrice")),
                    iconBgColor: "#ffc8c8",
                    iconColor: "#510606"
                },
            ],
        }

        setData(obj)
        dispatch(setShowLoading(false))
    }

    if (!data) return

    return (
        <Stack width="100%" spacing={2}>

            <Payments
                data={data?.payments}
                revenueForecast={data?.revenueForecast}
                expensesForecast={data?.expensesForecast}
                setDialogTitle={setDialogTitle}
                getData={getData}
            />

            <Documents
                data={data?.documents}
                setDialogTitle={setDialogTitle}
                getData={getData}
            />

            <Retainers
                data={data?.retainers}
                setDialogTitle={setDialogTitle}
                getData={getData}
            />

            <Bizs
                data={data?.bizStatus}
                buildCustomer={data?.buildCustomer}
                setDialogTitle={setDialogTitle}
                getData={getData}
            />

            <Mcs
                data={data?.mcStatus}
                setDialogTitle={setDialogTitle}
                getData={getData}
            />

            <ServiceCall
                data={data?.serviceCallStatus}
                setDialogTitle={setDialogTitle}
                getData={getData}
            />

            <NullCheck valueForCheck={bizDetails}>
                <BizsDetails
                    data={{ bizList: bizDetails, mcList: data?.mcList }}
                    dialogTitle={dialogTitle}
                    setData={setBizDetails}
                />
            </NullCheck>

            <NullCheck valueForCheck={mcDetails}>
                <McsDetails dialogTitle={dialogTitle} data={mcDetails} setData={setMcDetails} />
            </NullCheck>

            <NullCheck valueForCheck={serviceCallDetails}>
                <ServiceCallDetails dialogTitle={dialogTitle} data={serviceCallDetails} setData={setServiceCallDetails} />
            </NullCheck>

            <NullCheck valueForCheck={paymentsDetails}>
                <PaymentsDetails dialogTitle={dialogTitle} data={paymentsDetails} setData={setPaymentsDetails} />
            </NullCheck>

            <NullCheck valueForCheck={revenueForecast}>
                <RevenueForecast dialogTitle={dialogTitle} data={revenueForecast} setData={setRevenueForecast} />
            </NullCheck>

            <NullCheck valueForCheck={documentsDetails}>
                <DocumentsDetails dialogTitle={dialogTitle} data={documentsDetails} setData={setDocumentsDetails} />
            </NullCheck>
        </Stack >

    )
}
export default System